import React from 'react';
import { RefreshCw, X } from 'lucide-react';
import { CLR, fmtMoney, relativeDay } from './crmAnalyticsUi';

/**
 * The rows behind a CRM Analytics number.
 *
 * The backend returns several row shapes (event, login, contact, agent, lead,
 * property, deal) under one envelope, so this renders by `kind` — one modal for
 * every drill-down instead of one per metric.
 *
 * Note rows deliberately carry names and metadata only, never note text: an
 * agent's notes on a contact are private to them elsewhere in the CRM, and a
 * drill-down must not become a way around that.
 */

const MODULE_LABELS = {
    database: 'Database', calls: 'Calls', sms: 'SMS', email: 'Email',
    pipeline: 'Pipeline', tasks: 'Tasks', inspections: 'Inspections',
    appraisals: 'Appraisals', esign: 'eSign', campaigns: 'Campaigns',
};

const primaryText = (r) => {
    switch (r.kind) {
        case 'contact': return r.name || 'Unnamed contact';
        case 'agent': return r.name || 'Unnamed agent';
        case 'lead': return r.name || 'Unnamed lead';
        case 'login': return r.agent_name || 'Unknown user';
        case 'deal':
        case 'property': return r.address || `Property ${r.id}`;
        default:
            return r.entity_label || MODULE_LABELS[r.module] || r.event_type || 'Activity';
    }
};

const secondaryText = (r) => {
    const parts = (() => {
        switch (r.kind) {
            case 'contact': return [r.agent_name, r.contact_type, r.email];
            case 'agent': return [r.engagement_label,
                r.active_days != null ? `${r.active_days} active days` : null,
                r.total_actions != null ? `${r.total_actions} actions` : null];
            case 'lead': return [r.agent_name, r.stage, r.source, r.address];
            case 'login': return [r.action === '2fa_success' ? 'Signed in (2FA)' : 'Signed in',
                r.ip_address];
            case 'deal': return [r.agent_name, r.source === 'rea' ? 'REA feed' : 'CRM'];
            case 'property': return [r.agent_name, r.suburb, r.state, r.status];
            default: {
                const move = r.meta && r.meta.from && r.meta.to
                    ? `${r.meta.from} → ${r.meta.to}` : null;
                return [r.agent_name, MODULE_LABELS[r.module] || r.module, move,
                    r.source === 'legacy' ? null : r.source];
            }
        }
    })();
    return parts.filter(Boolean).join(' • ');
};

// Fixed display order + labels for the Pipeline Value breakdown (CG,
// 2026-08-28: "Break the lists up to On Market Selling, Off Market Selling,
// Appraisal"). Keyed off the `bucket` field the backend only attaches for
// metric === 'pipeline_value' - every other drill-down's rows have no
// bucket, so they render as the plain flat list below unchanged.
const PIPELINE_BUCKETS = [
    { key: 'on_market', label: 'On Market Selling' },
    { key: 'off_market', label: 'Off Market Selling' },
    { key: 'appraisal', label: 'Appraisal' },
];

const RowItem = ({ r }) => (
    <li className="py-2.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800 truncate">{primaryText(r)}</div>
            <div className="text-[11px] text-gray-500 truncate">{secondaryText(r)}</div>
        </div>
        <div className="text-right flex-shrink-0">
            {r.commission != null && (
                <div className="text-sm font-semibold tabular-nums" style={{ color: CLR.green }}>
                    {fmtMoney(r.commission)}
                </div>
            )}
            {r.price && r.commission == null && (
                <div className="text-sm text-gray-700 tabular-nums">{fmtMoney(r.price)}</div>
            )}
            {r.utilisation_score != null && (
                <div className="text-sm font-semibold text-gray-800 tabular-nums">
                    {r.utilisation_score}/100
                </div>
            )}
            {r.active_days_pct != null && r.utilisation_score == null && (
                <div className="text-sm font-semibold text-gray-800 tabular-nums">
                    {r.active_days_pct}%
                </div>
            )}
            {r.date && (
                <div className="text-[11px] text-gray-400">
                    {r.kind === 'agent' ? relativeDay(r.date) : r.date}
                </div>
            )}
        </div>
    </li>
);

const CrmDetailModal = ({
    open, onClose, title, loading, rows = [], total, truncated,
    metric, agentId, agents, onAgentChange,
}) => {
    if (!open) return null;
    const grouped = metric === 'pipeline_value' && rows.some((r) => r.bucket);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
                        {!loading && total != null && (
                            <p className="text-[11px] text-gray-500">
                                {truncated
                                    ? `Showing the first ${rows.length} of ${total.toLocaleString()}`
                                    : `${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {agents && agents.length > 0 && (
                    <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-2">
                        <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                            Agent
                        </span>
                        <select
                            className="text-sm border border-gray-200 rounded-lg px-2 py-1 flex-1 min-w-0"
                            value={agentId || ''}
                            onChange={(e) => onAgentChange && onAgentChange(e.target.value || null)}
                        >
                            <option value="">All agents</option>
                            {agents.map((a) => (
                                <option key={a.agent_id} value={a.agent_id}>{a.agent_name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-sm text-gray-500 py-10">No records in this period.</p>
                    ) : grouped ? (
                        PIPELINE_BUCKETS.map(({ key, label }) => {
                            const bucketRows = rows.filter((r) => r.bucket === key);
                            if (bucketRows.length === 0) return null;
                            const subtotal = bucketRows.reduce((sum, r) => sum + (Number(r.price) || 0), 0);
                            return (
                                <div key={key} className="mb-4 last:mb-0">
                                    <div className="flex items-center justify-between px-1 mb-1">
                                        <h4 className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                                            {label} <span className="text-gray-400 font-normal">({bucketRows.length})</span>
                                        </h4>
                                        {subtotal > 0 && (
                                            <span className="text-[11px] font-semibold text-gray-500 tabular-nums">
                                                {fmtMoney(subtotal)}
                                            </span>
                                        )}
                                    </div>
                                    <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                                        {bucketRows.map((r, idx) => (
                                            <RowItem key={`${r.kind}-${r.id ?? idx}`} r={r} />
                                        ))}
                                    </ul>
                                </div>
                            );
                        })
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {rows.map((r, idx) => (
                                <RowItem key={`${r.kind}-${r.id ?? idx}`} r={r} />
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CrmDetailModal;
